import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Vehicle } from "../../../../api/vehiclesApi";
import { formatRawSpeed } from "../../../../utils/speedUtils";

interface DisconnectedPanelProps {
  disconnectedDriving: Vehicle[];
}

const DisconnectedPanel: React.FC<DisconnectedPanelProps> = ({
  disconnectedDriving,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();

  if (disconnectedDriving.length === 0) return null;

  const handleListWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.scrollTop += event.deltaY;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 0.75,
        bgcolor: "background.paper",
        width: "100%",
        maxHeight: "calc(100vh - 48px)",
        overflow: "hidden",
        opacity: 0.95,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="body2"
          color="warning.main"
          sx={{ fontWeight: 600 }}
        >
          Disconnected Drivers ({disconnectedDriving.length})
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ width: 28, height: 28 }}
        >
          <ExpandMoreIcon
            sx={{
              transform: isExpanded ? "none" : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          />
        </IconButton>
      </Stack>

      <Collapse in={isExpanded} unmountOnExit>
        <Stack
          spacing={0.75}
          onWheelCapture={handleListWheel}
          onTouchMove={(event) => event.stopPropagation()}
          sx={{
            mt: 0.75,
            maxHeight: "calc(100vh - 116px)",
            overflowY: "auto",
            overscrollBehavior: "contain",
            pr: 0.5,
          }}
        >
          {disconnectedDriving.map((vehicle) => (
            <Box
              key={vehicle.id}
              sx={{
                p: 0.75,
                border: 1,
                borderColor: "warning.main",
                borderRadius: 1,
                bgcolor:
                  theme.palette.mode === "light"
                    ? "status.warningLight"
                    : "status.warningLight",
              }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1.25 }}>
                {vehicle.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Speed: {formatRawSpeed(vehicle.lastSpeed)}
              </Typography>
              {vehicle.lastLocationUpdate && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Last Update:{" "}
                  {new Date(vehicle.lastLocationUpdate).toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
};

export default DisconnectedPanel;
